import { createSelector } from "reselect"

import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Change control — the ONE queue of master-data versions awaiting approval (SPEC-058 B; BE
// SPEC-059). The backend aggregates every `draft` / `reviewer-approved` version across the four
// F7 families (spec versions, worklist templates, SST templates, forskrift versions):
//
//   GET /lims/change-control/pending → {items:[{kind, id, parentId, analysisDefinitionId, name,
//        versionNumber, state, requiredCapability, createdBy, createdByInitials, createdAt,
//        reviewerSignerId, basedOnVersionNumber, blockedReason, actionableByMe}]}
//        // oldest first; createdAt null for templates. SPEC-070: blockedReason null / "basePending" /
//        // "outOfDate", and actionableByMe is false while it is set.
//
// `actionableByMe` mirrors the server's sign() rule (capability held AND not in the four-eyes
// set) and is a DISPLAY HINT: the approve routes of each family remain the authority. This slice
// only LISTS; approving dispatches the family's own approve thunk (limsSpecVersions /
// limsWorklistTemplates / limsSstTemplates / limsForskrifter) and then re-fetches the queue.
//
// The full list is fetched once and shared: the Change-control register shows everything, the
// reviews inbox derives "mine" from `actionableByMe` (the server's `?scope=mine` filter exists
// too, but one request serving both surfaces keeps them consistent).

export const KINDS = {
  SPEC: "specVersion",
  WORKLIST: "worklistTemplate",
  SST: "sstTemplate",
  FORSKRIFT: "forskrift",
}

export const PENDING_STATES = { DRAFT: "draft", REVIEWER_APPROVED: "reviewer-approved" }

export function kindLabelKey(kind) {
  switch (kind) {
    case KINDS.SPEC:
      return "limsChangeControlKindSpec"
    case KINDS.WORKLIST:
      return "limsChangeControlKindWorklist"
    case KINDS.SST:
      return "limsChangeControlKindSst"
    case KINDS.FORSKRIFT:
      return "limsChangeControlKindForskrift"
    default:
      return "limsChangeControlKind"
  }
}

const initialState = { items: [], isLoading: false, hasFetched: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsChangeControl/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsChangeControl/fetch":
      return { items: action.res || [], isLoading: false, hasFetched: true }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsChangeControl/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/change-control/pending").then((res) => res.items)
})

export const selectPending = (state) => state.limsChangeControl.items
export const selectHasFetched = (state) => state.limsChangeControl.hasFetched

// What the CURRENT user can sign right now — the inbox view.
export const selectMine = createSelector([selectPending], (items) => items.filter((item) => item.actionableByMe))
export const selectMineCount = (state) => selectMine(state).length

export const selectItemById = (state, id) => selectPending(state).find((item) => item.id === id) || null

// SPEC-070: the translation key for why NOBODY can approve an item yet — its base is still
// awaiting approval, or it was built on something no longer in force — or null. The server
// computes `blockedReason` with the same rule its approve routes enforce (409).
export function blockedReasonKey(item) {
  if (item.blockedReason === "basePending") return "limsVersionBlockedBasePending"
  if (item.blockedReason === "outOfDate") return "limsVersionBlockedOutOfDate"
  return null
}
